import { ObservableQuery } from '@apollo/client';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Draggable, DraggableProvided } from 'react-beautiful-dnd';

import { client } from '../../../async/utils/client';
import { FEED_QUESTION_DETAILS_FRAGMENT } from '../../../graphql/fragments/feed/feedQuestionDetails';
import {
  ChangeQuestionPositionMutation,
  FeedBoardResponseFragment,
  FeedQuestionDetailsFragment,
  FetchQuestionsResponse,
  GetFeedBoardsQuery,
  GetFeedQuestionsInBoardQuery,
  GetQuestionsSubscriptionSubscription,
  GetQuestionsSubscriptionSubscriptionVariables,
  PlexOverviewFragment,
  useChangeQuestionPositionMutation,
  useGetFeedQuestionsInBoardQuery,
} from '../../../graphql/gen/types';
import { GET_QUESTIONS_SUBSCRIPTION } from '../../../graphql/subscriptions/GetQuestions';
import { usePublishStatusFilter } from '../../../models/filters/usePublishStatusFilter';
import { useSelectedLabelIds } from '../../../models/filters/useSelectedLabelIds';
import { useSelectedPlexQuestion } from '../details/question/useSelectedQuestion';
import { useDialogState } from '../dialog/useDialogState';
import { BoardView } from './BoardView';
import { optimisticPositionUpdate } from './optimisticPositionUpdate';
import { updateWithSubscriptionItem } from './updateWithSubscriptionItem';

type Props = {
  board: FeedBoardResponseFragment;
  index: number;
  plexData: PlexOverviewFragment;
  mode: 'single_plex' | 'multiple_plexes';
  totalFaqCount: number;
  nextCursor?: number | null;
  collapsed: boolean;
  onCollapseClicked: (event: React.MouseEvent<HTMLElement, MouseEvent>, boardId: string, collapsed: boolean) => void;
  boardSubscribeToMore: ObservableQuery['subscribeToMore'];
};

function updateBoardWithNewItem(
  boardId: string,
  prev: GetFeedBoardsQuery,
  updateFn: (board: FeedBoardResponseFragment) => FeedBoardResponseFragment
): GetFeedBoardsQuery {
  return {
    ...prev,
    loggedIn: {
      ...prev.loggedIn,
      feedBoards: {
        ...prev.loggedIn?.feedBoards,
        items:
          prev.loggedIn?.feedBoards?.items.map((b) => {
            if (b.id !== boardId) {
              // do nothing
              return b;
            } else {
              return updateFn(b);
            }
          }) || [],
      },
    },
  };
}

export function BoardViewContainer(props: Props): React.ReactElement | null {
  const {
    board,
    index,
    plexData,
    totalFaqCount,
    nextCursor,
    collapsed,
    onCollapseClicked,
    boardSubscribeToMore,
    mode,
  } = props;
  const [, setDialogState] = useDialogState();
  const [, setSelectedPlexQuestion] = useSelectedPlexQuestion();
  const plexRelation = plexData.currentUserRelation.state;
  const [loadingMore, setLoadingMore] = useState(false);

  const handleSelectPlexQuestion = useCallback(
    (permalink: string): void => {
      setSelectedPlexQuestion({ permalink, commentsVisible: false });
    },
    [setSelectedPlexQuestion]
  );

  const handleEdit = useCallback((): void => {
    setDialogState({ type: 'edit_board', boardId: board.id });
  }, [board.id, setDialogState]);

  const [orderDuringDrag, setOrderDuringDrag] = useState<{ dragIndex: number; hoverIndex: number } | null>(null);

  const questions = useMemo(() => board.questions.items.filter((question) => !question.deleted).sort(faqsCompare), [
    board.questions.items,
  ]);

  const order = useMemo(() => {
    const order = questions.map((_, i) => i);

    if (orderDuringDrag != null) {
      const { dragIndex, hoverIndex } = orderDuringDrag;

      const alteredOrder = [...order];
      alteredOrder.splice(dragIndex, 1);
      alteredOrder.splice(hoverIndex, 0, order[dragIndex]);

      return alteredOrder;
    }

    return order;
  }, [orderDuringDrag, questions]);

  const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
    // The `moveCard` API provides each move incrementally, while orderDuringDrag stores
    // the first dragIndex and the current hover position
    setOrderDuringDrag((orderDuringDrag) =>
      orderDuringDrag == null ? { dragIndex, hoverIndex } : { ...orderDuringDrag, hoverIndex }
    );
  }, []);

  const onDragEnd = useCallback(() => {
    setOrderDuringDrag(null);
  }, []);

  const [changePosition] = useChangeQuestionPositionMutation({ fetchPolicy: 'no-cache' });

  const onDrop = (question: FeedQuestionDetailsFragment, atIndex: number): void => {
    setOrderDuringDrag(null);

    const newPosition = questions[atIndex].positionInBoard.positionInBoard;

    changePosition({
      variables: {
        input: { questionId: question.id, newPosition },
      },
      optimisticResponse: optimisticUpdate({ questions, question, newPosition }),
    });
  };

  const filterPublishStatus = usePublishStatusFilter();
  const labelIds = useSelectedLabelIds();
  const variables = useMemo(() => ({ boardId: board.id, filterPublishStatus, labelIds }), [
    filterPublishStatus,
    labelIds,
    board.id,
  ]);
  // TODO: handle errors
  const { fetchMore } = useGetFeedQuestionsInBoardQuery({
    skip: true,
    fetchPolicy: 'standby',
  });

  const onFetchMore = useCallback(
    (nextCursor: number) => {
      setLoadingMore(true);
      fetchMore({
        variables: { ...variables, cursor: nextCursor },
        updateQuery: (prev: GetFeedQuestionsInBoardQuery, { fetchMoreResult }) => {
          client.cache.modify({
            id: client.cache.identify({ __typename: 'Board', id: board.id }),
            fields: {
              questions(existingQuestionsRefs): FetchQuestionsResponse {
                const newRefs = fetchMoreResult?.loggedIn?.boardById?.questions?.items?.map((q) =>
                  client.cache.writeFragment({
                    data: q,
                    fragment: FEED_QUESTION_DETAILS_FRAGMENT,
                    fragmentName: 'feedQuestionDetails',
                  })
                );

                return {
                  items: [...(existingQuestionsRefs?.items || []), ...(newRefs || [])],
                  nextCursor: fetchMoreResult?.loggedIn?.boardById?.questions?.nextCursor,
                };
              },
            },
          });
          if (!fetchMoreResult || !fetchMoreResult.loggedIn?.boardById?.questions) return prev;
          if (fetchMoreResult.loggedIn?.boardById?.questions?.items.length !== 1) return prev;
          return prev;
        },
      }).finally(() => {
        setLoadingMore(false);
      });
    },
    [board.id, fetchMore, variables]
  );

  useEffect(() => {
    const unsubscribe = boardSubscribeToMore<
      GetQuestionsSubscriptionSubscription,
      GetQuestionsSubscriptionSubscriptionVariables
    >({
      document: GET_QUESTIONS_SUBSCRIPTION,
      variables: { boardId: board.id, filterPublishStatus, labelIds },
      updateQuery: (
        prev: GetFeedBoardsQuery,
        { subscriptionData }: { subscriptionData: { data: GetQuestionsSubscriptionSubscription } }
      ) => {
        const newItem = subscriptionData.data.question;
        if (!newItem) return prev;
        return updateBoardWithNewItem(newItem.boardId, prev, (b) => {
          const minimumQuestionPosition = b.questions.nextCursor || 0;
          const shouldUpdateItem = newItem.positionInBoard.positionInBoard > minimumQuestionPosition;

          if (shouldUpdateItem) {
            return {
              ...b,
              questions: {
                ...b.questions,
                items: updateWithSubscriptionItem({
                  newItem,
                  existingItems: b.questions.items.filter(
                    (q) => q.positionInBoard.positionInBoard > minimumQuestionPosition
                  ),
                  newItemLocation: 'front',
                }),
              },
            };
          } else {
            return b;
          }
        });
      },
    });

    return unsubscribe;
  }, [board.id, boardSubscribeToMore, filterPublishStatus, labelIds]);

  if (!plexRelation) {
    return null;
  }

  if (!questions.length && mode === 'multiple_plexes') {
    // hide board if no plex is selected and the board has no questions
    // we still have to create the component in order to subscribe to question
    // updates, in case there is a new question added/moved to this board
    return null;
  }

  const boardView = (draggableProvided?: DraggableProvided): React.ReactElement => (
    <BoardView
      board={board}
      order={order}
      draggableProvided={draggableProvided}
      onSelectPlexQuestion={handleSelectPlexQuestion}
      plexRelation={plexRelation}
      plexData={plexData}
      totalFaqCount={totalFaqCount}
      nextCursor={nextCursor}
      collapsed={collapsed}
      onCollapseClicked={onCollapseClicked}
      onEditBoard={handleEdit}
      onMoveCard={moveCard}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      questionsArr={questions}
      loadingMore={loadingMore}
      onFetchMore={onFetchMore}
    />
  );

  switch (mode) {
    case 'single_plex': {
      return (
        <Draggable draggableId={board.id} index={index}>
          {boardView}
        </Draggable>
      );
    }
    case 'multiple_plexes': {
      return boardView();
    }
  }
}

const faqsCompare = (question1: FeedQuestionDetailsFragment, question2: FeedQuestionDetailsFragment): number => {
  // Show in DESC order of `positionInPlex`
  return -(question1.positionInBoard.positionInBoard - question2.positionInBoard.positionInBoard);
};

const optimisticUpdate = ({
  questions,
  question,
  newPosition,
}: {
  questions: FeedQuestionDetailsFragment[];
  question: FeedQuestionDetailsFragment;
  newPosition: number;
}): ChangeQuestionPositionMutation => {
  return {
    __typename: 'Mutation',
    changeQuestionPosition: {
      __typename: 'QuestionPositionUpdate',
      positions: optimisticPositionUpdate<FeedQuestionDetailsFragment>({
        entity: question,
        entities: questions,
        newPosition,
        getPosition: (question: FeedQuestionDetailsFragment) => question.positionInBoard.positionInBoard,
      }).map(({ id, updatedPosition }) => ({
        __typename: 'QuestionPosition',
        id,
        positionInBoard: updatedPosition,
      })),
    },
  };
};
